<template>
  <button
    :type="typeButton"
    :class="buttonVariants({ theme })"
    class="text-avenir my-6 flex h-[48px] w-full items-center justify-center px-6 py-3 font-bold disabled:cursor-not-allowed disabled:border-[#ADBACB] disabled:bg-[#ADBACB] lg:h-[60px] lg:w-fit"
  >
    <div
      v-show="isLoading"
      class="loader border-t-pink absolute left-0 right-0 m-auto h-6 w-6 animate-spin rounded-full border-4 border-t-4 border-gray-200 ease-linear"
    ></div>
    <span :class="[isLoading && 'blur-sm']">{{ title }}</span>
    <component v-if="resolvedIcon" :is="resolvedIcon" class="ml-2 h-5 w-5" />
  </button>
</template>

<script setup lang="ts">
import { THEMES } from 'refresh-ui'
import { cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

type IconType = 'arrowRight' // Update when adding more icons

interface IButtonProps {
  title: string
  type?: 'fill' | 'outline' // Can type be converted to variant?
  typeButton?: 'submit' | 'reset' | 'button'
  variant?: 'primary' | 'secondary' | 'generic'
  isCta?: boolean
  useDarkText?: boolean
  theme?: THEMES
  isLoading?: boolean
  iconType?: IconType
}

const props = withDefaults(defineProps<IButtonProps>(), {
  type: 'fill',
  typeButton: 'button',
  variant: 'primary',
  isCta: true,
  theme: THEMES.DEFAULT,
  isLoading: false,
  arrowRight: false,
})

const icons = {
  [THEMES.DJURENS_RATT]: {
    // TODO: Add icons for Djure's Rätt theme
    arrowRight: () => import('../atoms/Icons/ArrowRightRdk.vue'),
  },
  [THEMES.RDK]: {
    // TODO: Add icons for RDK theme
    arrowRight: () => import('../atoms/Icons/ArrowRightRdk.vue'),
  },
  [THEMES.DEFAULT]: {
    // TODO: Add icons for default theme
    arrowRight: () => import('../atoms/Icons/ArrowRightRdk.vue'),
  },
}

const resolvedIcon = shallowRef()

onMounted(async () => {
  if (props.iconType && icons[props.theme]?.[props.iconType]) {
    resolvedIcon.value = (await icons[props.theme][props.iconType]()).default
  } else {
    resolvedIcon.value = undefined // Reset if iconType is invalid
  }
})

const buttonVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DJURENS_RATT]:
        props.variant === 'primary'
          ? twMerge(
              'uppercase shadow-[0_5px_15px_0px_rgba(171,8,98,0.22)] hover:shadow-none',
              props.type === 'fill'
                ? 'bg-pink text-white hover:bg-pink-dark border-2 border-pink hover:border-pink-dark'
                : 'bg-white text-pink border-2 border-pink hover:border-pink-dark hover:text-pink-dark',
            )
          : props.variant === 'secondary'
            ? twMerge(
                'uppercase shadow-[0_5px_15px_0px_rgba(171,8,98,0.22)] hover:shadow-none',
                props.type === 'fill'
                  ? 'bg-blue text-white hover:bg-blue-dark border-2 border-blue hover:border-blue-dark'
                  : 'bg-white text-blue border-2 border-blue hover:border-blue-dark hover:text-blue-dark',
              )
            : 'uppercase bg-blue text-white hover:bg-blue-dark border-2 border-blue hover:border-blue-dark shadow-[0_5px_15px_0px_rgba(171,8,98,0.22)] hover:shadow-none',
      [THEMES.RDK]:
        props.variant === 'primary'
          ? twMerge(
              'rounded-full',
              props.isCta
                ? 'bg-pink text-white w-full hover:bg-pink-dark border-2 border-pink hover:border-pink-dark'
                : 'bg-[#ffe2f3] text-pink border-2 border-[#ffe2f3] hover:border-pink-dark hover:text-pink-dark',
            )
          : props.variant === 'secondary'
            ? twMerge(
                'rounded-full',
                props.isCta
                  ? 'bg-[#5f4198] text-white hover:bg-[#2a0179] border-2 border-[#5f4198] hover:border-[#2a0179]'
                  : 'bg-[#e6daff] text-[#5f4198] border-2 border-[#e6daff] hover:border-[#2a0179] hover:text-[#2a0179]',
              )
            : twMerge(
                'rounded-full bg-white border-2 border-white',
                props.useDarkText
                  ? 'text-[#5F4198] hover:bg-[#e6daff] hover:border-[#e6daff]'
                  : 'text-pink hover:bg-[#ffe2f3] hover:border-[#ffe2f3]',
              ),
      [THEMES.DEFAULT]:
        props.variant === 'primary'
          ? twMerge(
              'rounded-full',
              props.isCta
                ? 'bg-pink text-white hover:bg-pink-dark border-2 border-pink hover:border-pink-dark'
                : 'bg-[#ffe2f3] text-pink border-2 border-[#ffe2f3] hover:border-pink-dark hover:text-pink-dark',
            )
          : props.variant === 'secondary'
            ? twMerge(
                'rounded-full',
                props.isCta
                  ? 'bg-[#5f4198] text-white hover:bg-[#2a0179] border-2 border-[#5f4198] hover:border-[#2a0179]'
                  : 'bg-[#e6daff] text-[#5f4198] border-2 border-[#e6daff] hover:border-[#2a0179] hover:text-[#2a0179]',
              )
            : twMerge(
                'rounded-full bg-white border-2 border-white',
                props.useDarkText
                  ? 'text-[#5F4198] hover:bg-[#e6daff] hover:border-[#e6daff]'
                  : 'text-pink hover:bg-[#ffe2f3] hover:border-[#ffe2f3]',
              ),
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
